import { portalAPI } from './portalAPI';

import type { InvoiceComments, InvoiceCommentWrite } from 'types/invoiceComments';

type CommentId = { invoiceId: number; commentId: number };

const APIInvoiceCommentsSlice = portalAPI.enhanceEndpoints({ addTagTypes: ['InvoiceComments'] }).injectEndpoints({
    endpoints: (builder) => ({
        getInvoiceComments: builder.query<InvoiceComments, number>({
            query: (invoiceId) => `/v1/invoices/${invoiceId}/comments`,
            providesTags: (_result, _error, invoiceId) => [{ type: 'InvoiceComments', id: invoiceId }],
        }),
        createInvoiceComment: builder.mutation<
            number,
            { invoiceId: number; parentId: number | null } & InvoiceCommentWrite
        >({
            query: ({ invoiceId, ...body }) => ({
                url: `/v1/invoices/${invoiceId}/comments`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { invoiceId }) => [{ type: 'InvoiceComments', id: invoiceId }],
        }),
        updateInvoiceComment: builder.mutation<void, CommentId & InvoiceCommentWrite>({
            query: ({ commentId, text, mentions }) => ({
                url: `/v1/invoice-comments/${commentId}`,
                method: 'PUT',
                body: { text, mentions },
            }),
            invalidatesTags: (_result, _error, { invoiceId }) => [{ type: 'InvoiceComments', id: invoiceId }],
        }),
        setInvoiceCommentHandled: builder.mutation<void, CommentId & { handled: boolean }>({
            query: ({ commentId, handled }) => ({
                url: `/v1/invoice-comments/${commentId}/handled`,
                method: handled ? 'PUT' : 'DELETE',
            }),
            invalidatesTags: (_result, _error, { invoiceId }) => [{ type: 'InvoiceComments', id: invoiceId }],
        }),
        deleteInvoiceComment: builder.mutation<void, CommentId>({
            query: ({ commentId }) => ({
                url: `/v1/invoice-comments/${commentId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { invoiceId }) => [{ type: 'InvoiceComments', id: invoiceId }],
        }),
        // The picked employees who could not open this invoice's comments, so would not be notified.
        findMentionsWithoutAccess: builder.query<number[], { invoiceId: number; employeeIds: number[] }>({
            query: ({ invoiceId, employeeIds }) => ({
                url: `/v1/invoices/${invoiceId}/comments/mention-access`,
                method: 'POST',
                body: { employeeIds },
            }),
            // A read sent as a POST: it must not hold, nor release, the lock a write puts on the submit buttons.
            extraOptions: { skipButtonDisable: true },
        }),
    }),
});

export const {
    useGetInvoiceCommentsQuery,
    useCreateInvoiceCommentMutation,
    useUpdateInvoiceCommentMutation,
    useSetInvoiceCommentHandledMutation,
    useDeleteInvoiceCommentMutation,
    useFindMentionsWithoutAccessQuery,
} = APIInvoiceCommentsSlice;
